import { Fragment, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import {
  formatEuros,
  type BillingLineDraft,
  type BillingLineUnit,
} from '../../services/billing/billingService';
import {
  useBillingLineItems,
  useReplaceBillingLines,
} from '../../hooks/queries/useBillingDocuments';
import {
  computeLineTotals,
  formatVatRate,
  LINE_NATURE_LABELS,
  VAT_RATE_OPTIONS,
  type LineNature,
} from '../../lib/billing/lineTotals';

interface Props {
  documentId: string;
  vatRate: number;
  onClose: () => void;
}

const UNIT_OPTIONS: { value: BillingLineUnit; label: string }[] = [
  { value: 'hour', label: 'h' },
  { value: 'item', label: 'unité' },
  { value: 'forfait', label: 'forfait' },
];

interface DraftLine extends BillingLineDraft {
  _key: string;
  id?: string;
}

function newEmptyLine(sort: number): DraftLine {
  return {
    _key: crypto.randomUUID(),
    description: '',
    quantity: 1,
    unit: 'item',
    unit_price: 0,
    sort_order: sort,
    time_entry_id: null,
    nature: 'honoraires',
    vat_rate: null,
  };
}

export function BillingLinesEditor({ documentId, vatRate, onClose }: Props) {
  const { data: serverLines } = useBillingLineItems(documentId);
  const replaceMut = useReplaceBillingLines(documentId);

  const [lines, setLines] = useState<DraftLine[]>([]);

  // Charge les lignes du serveur au montage / quand elles changent
  useEffect(() => {
    if (!serverLines) return;
    setLines(
      serverLines.map((l) => ({
        _key: l.id,
        id: l.id,
        description: l.description,
        quantity: Number(l.quantity),
        unit: l.unit as BillingLineUnit,
        unit_price: Number(l.unit_price),
        sort_order: l.sort_order,
        time_entry_id: l.time_entry_id,
        nature: l.nature as LineNature,
        vat_rate: l.vat_rate === null ? null : Number(l.vat_rate),
      })),
    );
  }, [serverLines]);

  // Même calcul que la base : TVA arrondie par taux.
  const totals = useMemo(() => computeLineTotals(lines, vatRate), [lines, vatRate]);

  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((prev) => prev.map((l) => (l._key === key ? { ...l, ...patch } : l)));
  }

  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l._key !== key));
  }

  function addLine() {
    setLines((prev) => [...prev, newEmptyLine(prev.length + 1)]);
  }

  function move(key: string, dir: -1 | 1) {
    setLines((prev) => {
      const idx = prev.findIndex((l) => l._key === key);
      if (idx < 0) return prev;
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const arr = [...prev];
      const tmp = arr[idx]!;
      arr[idx] = arr[newIdx]!;
      arr[newIdx] = tmp;
      return arr;
    });
  }

  async function save() {
    // Resort + validate
    const cleaned: BillingLineDraft[] = lines
      .filter((l) => l.description.trim().length > 0)
      .map((l, i) => ({
        time_entry_id: l.time_entry_id ?? null,
        description: l.description.trim(),
        quantity: Number(l.quantity) || 0,
        unit: l.unit,
        unit_price: Number(l.unit_price) || 0,
        sort_order: i + 1,
        nature: l.nature ?? 'honoraires',
        vat_rate: l.vat_rate ?? null,
      }));

    if (cleaned.length === 0 && lines.length > 0) {
      toast.error('Toutes les lignes ont une description vide.');
      return;
    }

    try {
      await replaceMut.mutateAsync(cleaned);
      toast.success('Lignes enregistrées');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement');
    }
  }

  return (
    <Card
      header={
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-neutral-700">Édition des lignes</span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={replaceMut.isPending}>
              <X className="w-3.5 h-3.5 mr-1" /> Annuler
            </Button>
            <Button variant="primary" size="sm" onClick={save} isLoading={replaceMut.isPending}>
              <Save className="w-3.5 h-3.5 mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-2">
        {lines.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-4">
            Aucune ligne. Cliquez sur « Ajouter une ligne ».
          </p>
        ) : (
          <table className="cime-table text-sm">
            <thead>
              <tr>
                <th className="py-2 px-2 w-10"></th>
                <th className="py-2 px-2 w-[128px]">Nature</th>
                <th className="py-2 px-2 w-[72px] text-right">Qté</th>
                <th className="py-2 px-2 w-[92px]">Unité</th>
                <th className="py-2 px-2 w-[96px] text-right">PU HT</th>
                <th className="py-2 px-2 w-[90px]">TVA</th>
                <th className="py-2 px-2 w-[96px] text-right">Total HT</th>
                <th className="py-2 px-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const lineHt = Number((l.quantity * l.unit_price).toFixed(2));
                const isTimeEntry = !!l.time_entry_id;
                return (
                  <Fragment key={l._key}>
                  <tr className="align-top">
                    <td rowSpan={2} className="py-1.5 px-1 border-b border-neutral-100">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => move(l._key, -1)}
                          disabled={i === 0}
                          className="p-0.5 text-neutral-400 hover:text-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Monter"
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(l._key, 1)}
                          disabled={i === lines.length - 1}
                          className="p-0.5 text-neutral-400 hover:text-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Descendre"
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    <td colSpan={6} className="pt-1.5 px-1">
                      <textarea
                        value={l.description}
                        onChange={(e) => updateLine(l._key, { description: e.target.value })}
                        rows={Math.max(1, Math.ceil(l.description.length / 60))}
                        className="w-full px-2 py-1.5 text-sm rounded border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-cime-primary resize-y min-h-[2.25rem]"
                        placeholder="Prestation, honoraires, frais…"
                      />
                      {isTimeEntry && (
                        <p className="text-[10px] text-neutral-400 mt-0.5 italic">
                          Ligne issue d'une prestation
                        </p>
                      )}
                    </td>
                    <td rowSpan={2} className="py-1.5 px-1 border-b border-neutral-100">
                      <button
                        type="button"
                        onClick={() => removeLine(l._key)}
                        className="p-1 text-neutral-400 hover:text-red-600"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  <tr className="align-top border-b border-neutral-100">
                    <td className="py-1.5 px-1">
                      <select
                        value={l.nature ?? 'honoraires'}
                        onChange={(e) => updateLine(l._key, { nature: e.target.value as LineNature })}
                        aria-label="Nature de la ligne"
                        className="w-full h-9 px-2 text-sm rounded border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-cime-primary"
                      >
                        {(Object.keys(LINE_NATURE_LABELS) as LineNature[]).map((n) => (
                          <option key={n} value={n}>
                            {LINE_NATURE_LABELS[n]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={l.quantity}
                        onChange={(e) => updateLine(l._key, { quantity: Number(e.target.value) })}
                        className="w-full h-9 px-2 text-sm text-right tabular-nums rounded border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-cime-primary"
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <select
                        value={l.unit}
                        onChange={(e) =>
                          updateLine(l._key, { unit: e.target.value as BillingLineUnit })
                        }
                        className="w-full h-9 px-2 text-sm rounded border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-cime-primary"
                      >
                        {UNIT_OPTIONS.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={l.unit_price}
                        onChange={(e) => updateLine(l._key, { unit_price: Number(e.target.value) })}
                        className="w-full h-9 px-2 text-sm text-right tabular-nums rounded border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-cime-primary"
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <select
                        value={String(l.vat_rate ?? vatRate)}
                        onChange={(e) => {
                          // Le taux du document reste la valeur par défaut (NULL en base).
                          const rate = Number(e.target.value);
                          updateLine(l._key, { vat_rate: rate === vatRate ? null : rate });
                        }}
                        aria-label="Taux de TVA de la ligne"
                        className="w-full h-9 px-2 text-sm rounded border border-neutral-300 bg-white focus:outline-none focus:ring-1 focus:ring-cime-primary"
                      >
                        {[...new Set<number>([...VAT_RATE_OPTIONS, vatRate, ...(l.vat_rate != null ? [l.vat_rate] : [])])].map((rate) => (
                          <option key={rate} value={String(rate)}>
                            {formatVatRate(rate)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 px-1 text-right cime-amount align-middle">
                      {formatEuros(lineHt)}
                    </td>
                  </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="pt-2">
          <Button variant="secondary" size="sm" onClick={addLine}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne
          </Button>
        </div>

        <div className="pt-3 border-t border-neutral-200 space-y-1 text-sm max-w-sm ml-auto">
          <div className="flex justify-between">
            <span className="text-neutral-600">Total HT</span>
            <span className="cime-amount">{formatEuros(totals.ht)}</span>
          </div>
          {totals.byRate.length === 0 ? (
            <div className="flex justify-between">
              <span className="text-neutral-600">TVA ({formatVatRate(vatRate)})</span>
              <span className="cime-amount">{formatEuros(0)}</span>
            </div>
          ) : (
            totals.byRate.map((r) => (
              <div key={r.rate} className="flex justify-between">
                <span className="text-neutral-600">
                  TVA {formatVatRate(r.rate)}
                  {totals.byRate.length > 1 ? <span className="text-neutral-400"> sur {formatEuros(r.ht)}</span> : null}
                </span>
                <span className="cime-amount">{formatEuros(r.vat)}</span>
              </div>
            ))
          )}
          <div className="flex justify-between border-t border-neutral-300 pt-1 mt-1 font-semibold">
            <span>Total TTC</span>
            <span className="cime-amount">{formatEuros(totals.ttc)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
